import {useEffect, useRef, useState} from 'react'
import {useRouter} from 'next/router'
import _ from 'lodash'
import {setCookie} from 'cookies-next'
// Contexts
import {useCombinedAuthStatus} from '../../hooks/use-combined-auth-status'
// Custom hooks
import {useBoundRoute} from '../../hooks/use-bound-route'
// Components
import ClientText from '../client-text'
import UploaderMinimalStep from '../submission-wizard/uploader-minimal-step'
import UploaderInlineStep from '../submission-wizard/uploader-inline-step'
import AddMetadataStep from '../submission-wizard/add-metadata-step'
import PayWithStripeStep from '../submission-wizard/pay-with-stripe-step'
import DemonstrateSuccessStep from '../submission-wizard/demonstrate-success-step'
// Utils
import {citysparkOIDCLogin} from '../../utils/auth-helpers'
import {convertHoursToSeconds} from '../../utils/number-helpers'
import {getPendingAction, clearPendingAction, storePendingAction} from '../../utils/pending-action'
import {getSubmissionFeeCents} from '../../utils/submission-fee'
import {
  isNextDisabledForStep,
  isPreviousDisabledForStep,
  getGatedNextStepIndex,
} from './wizard-step-gate'
import {shouldResetPaymentProcessingOnClose} from '../payment/checkout-payment-flow'
// Types
import type {CSSProperties, ComponentType} from 'react'
import type {SubmissionObject, AdditionalFormField, BallotAdditionalFormField, StripePayload, UploadcareFile} from '../../types/submission-wizard'
import type {PhotoGallerySubmissionWizardProps, PreviousVote, WizardStep} from '../../types/photo-gallery'
// Styles
import ctaStyles from './photo-gallery-submission-cta.module.scss'
import styles from '../submission-wizard/submission-wizard.module.scss'

type SubmissionWizardVariant = 'scroll' | 'flow-grid' | 'flow-masonry'

const hiddenStyle: CSSProperties = {display: 'none'}

// Builds the default additional-form-field definitions from the ballot config.
// Shared by the stripePayload initializer and clearSubmissionForm so closing the
// wizard restores (rather than wipes) the required fields on the next open.
const buildDefaultAdditionalFormFields = (
  ballotAdditionalFormFields: BallotAdditionalFormField[] | undefined,
): AdditionalFormField[] =>
  _.map(ballotAdditionalFormFields, (formField: BallotAdditionalFormField) => {
    const value: string | boolean = formField.type === 'checkbox'
      ? (formField.checkbox_mark_by_default || false)
      : ''
    return {...formField, value}
  })

interface Props extends PhotoGallerySubmissionWizardProps {
  variant: SubmissionWizardVariant
  previousVote?: PreviousVote | null
  /** Grid variant passes container styles for the CTA card */
  gridContainerStyles?: CSSProperties
  isCentered?: boolean
}

const PhotoGallerySubmissionWizard = ({
  customer,
  ballot,
  question,
  variant,
  gridContainerStyles,
  isCentered = false,
  canSubmit,
  submissionIsProcessing,
  paymentIsProcessing,
  sendPaymentIsProcessing,
  onCastSubmission = null,
  onPaginate,
  sendModalIsIn,
}: Props) => {
  const boundRoute = useBoundRoute()
  const router = useRouter()
  const {voter} = useCombinedAuthStatus()

  // From ballot and question
  const allowsMultiplePhotosPerSubmission = ballot.ballot_settings?.no_of_photos !== 'single'

  // From question. Shared with the gallery's paid-vs-free branching via
  // getSubmissionFeeCents so the Pay-step gate and reconciliation logic agree.
  const paymentAmount = getSubmissionFeeCents(ballot)
  const successMessage = (
    paymentAmount
      ? ballot.ballot_settings?.submission_fee_settings?.confirmation_message || 'Your payment was successful. Please allow time for it to be reviewed'
      : (ballot?.photo_submission_settings as any)?.success_message || '<p>Your submission was successful. Please allow time for it to be reviewed</p>'
  )

  const ballotAdditionalFormFields = ballot.photo_submission_settings?.additional_form_fields
  const [stripePayload, setStripePayload] = useState<StripePayload>(() => {
    const additionalFormFields: AdditionalFormField[] = buildDefaultAdditionalFormFields(ballotAdditionalFormFields)
    return {
      paymentAmount: paymentAmount,
      groupSlug: question?.groupSlug ?? undefined,
      customerId: customer?._id,
      ballotId: ballot._id,
      ballotName: ballot.ballot_name,
      questionSlug: question?.slug ?? undefined,
      questionName: question?.name,
      voterEmail: voter?.email,
      voterName: [voter?.first_name, voter?.last_name].filter(Boolean).join(' '),
      submissionName: '',
      submissionUrl: '',
      additionalFormFields: additionalFormFields,
    }
  })
  const sendStripePayload = (value: StripePayload): void => {
    setStripePayload(value)
  }

  // The stripePayload initializer only runs on mount. Navigation between
  // groups/categories is shallow-routed (no remount), so resync the group and
  // category identity when the question prop changes to avoid submitting a
  // nomination under a previously-viewed group/category.
  useEffect(() => {
    setStripePayload(prev => ({
      ...prev,
      groupSlug: question?.groupSlug ?? undefined,
      questionSlug: question?.slug ?? undefined,
      questionName: question?.name,
    }))
  }, [
    question?.groupSlug,
    question?.slug,
    question?.name,
  ])

  // The initializer captures voter identity once on mount. If the voter loads
  // (or its name/email populates) asynchronously afterward, resync it so we
  // never submit a stale empty email or a "undefined undefined" name.
  useEffect(() => {
    setStripePayload(prev => ({
      ...prev,
      voterEmail: voter?.email,
      voterName: [voter?.first_name, voter?.last_name].filter(Boolean).join(' '),
    }))
  }, [
    voter?.email,
    voter?.first_name,
    voter?.last_name,
  ])

  const [uploadcareFiles, setUploadcareFiles] = useState<UploadcareFile[]>([])
  const sendUploadcareFiles = (value: UploadcareFile[]): void => setUploadcareFiles(value)

  const clearSubmissionForm = () => {
    let emptyStripePayload = _.cloneDeep(stripePayload)

    emptyStripePayload['submissionName'] = ''
    emptyStripePayload['submissionUrl'] = ''
    emptyStripePayload['additionalFormFields'] = buildDefaultAdditionalFormFields(ballotAdditionalFormFields)

    setStripePayload(emptyStripePayload)
    setUploadcareFiles([])
  }

  const [wizardIsVisible, setWizardIsVisible] = useState(false)

  // Resume a pending nomination after sign-in redirect
  const hasResumedPendingNomination = useRef(false)
  useEffect(() => {
    if (!voter?._id || !question?._id || hasResumedPendingNomination.current) return
    const pending = getPendingAction()
    if (pending?.type !== 'nomination' || pending.subCategoryId !== question._id) return

    hasResumedPendingNomination.current = true
    clearPendingAction()
    showWizard()
  }, [voter?._id, question?._id])

  const handleShowWizardClick = () => {
    if (!voter?._id) {
      setCookie('previous_pathname', router.asPath, {maxAge: convertHoursToSeconds(90), path: '/'})

      // Save the pending nomination so the wizard can auto-open after sign-in
      if (question?._id) {
        storePendingAction({
          type: 'nomination',
          subCategoryId: question._id,
          timestamp: Date.now(),
        })
      }

      const allowsUnauthenticatedSignIn = ballot?.general?.un_authenticated_emails_option_turn_on || false
      if (allowsUnauthenticatedSignIn) {
        const signinRoute = boundRoute('/auth/signin')
        if (signinRoute) router.push(signinRoute)
      } else {
        citysparkOIDCLogin({
          router,
          ballotId: ballot?._id,
          citysparkId: (customer as any)?.subscription?.business_registration?.cityspark_id || null,
        })
      }
    } else {
      showWizard()
    }
  }
  const showWizard = () => {
    setWizardIsVisible(true)
    sendModalIsIn(true)
  }
  const hideWizard = () => {
    setWizardIsVisible(false)
    sendModalIsIn(false)
  }
  const [currentStep, setCurrentStep] = useState(0)

  // Grid uses inline uploader; all other variants use minimal uploader
  const UploaderComponent: ComponentType<any> = variant === 'flow-grid'
    ? UploaderInlineStep
    : UploaderMinimalStep

  const steps: WizardStep[] = [
    {title: 'Upload images', component: UploaderComponent},
    {title: 'Add metadata', component: AddMetadataStep},
    {title: 'Demonstrate success', component: DemonstrateSuccessStep},
  ]
  if (paymentAmount) {
    steps.splice(-1, 0, {title: 'Pay with Stripe', component: PayWithStripeStep})
  }

  // Derive the step gates during render rather than storing them in state that
  // a useEffect updates after paint. Effect-driven state lagged by one render on
  // step change, letting a rapid second click act on the previous step's gate
  // and skip/submit a page before its validation engaged. Derived values are
  // never stale, and handleNext/handleSubmit re-check the gate so a click can
  // never advance past a still-blocked step. See ./wizard-step-gate.
  const stepTitles = steps.map(step => step.title)
  const currentStepTitle = stepTitles[currentStep]
  const wizardGateInput = {
    submissionName: stripePayload.submissionName,
    submissionUrl: stripePayload.submissionUrl,
    additionalFormFields: stripePayload.additionalFormFields,
    uploadcareFilesCount: uploadcareFiles.length,
  }
  const nextIsDisabled = isNextDisabledForStep({...wizardGateInput, stepTitle: currentStepTitle})
  const previousIsDisabled = isPreviousDisabledForStep({
    stepTitle: currentStepTitle,
    paymentIsProcessing,
    submissionIsProcessing,
  })
  // Block closing the wizard while a payment/submission is in flight. Otherwise
  // a user could close mid-payment and reopen to retry (duplicate-charge window),
  // and a late async completion could advance step state after close. Safe to
  // block because both flags are bounded: payment by CHECKOUT_TIMEOUT_MS (via
  // handlePaymentAttempt) and submission by SUBMISSION_TIMEOUT_MS (addVote's
  // AbortController), so neither can leave the user unable to recover.
  const closeIsDisabled = paymentIsProcessing || submissionIsProcessing

  const handleNext = () => {
    setCurrentStep(previousCurrentStep =>
      getGatedNextStepIndex(stepTitles, previousCurrentStep, wizardGateInput))
  }

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }

  const onClose = () => {
    clearSubmissionForm()
    // Closing must always clear a stuck payment flag; otherwise a locked pay
    // step stayed disabled on reopen until a full page reload. See
    // shouldResetPaymentProcessingOnClose in checkout-payment-flow.
    if (shouldResetPaymentProcessingOnClose()) sendPaymentIsProcessing(false)
    hideWizard()
    setCurrentStep(0)
  }

  const handleCloseAndPaginate = () => {
    onClose()
    onPaginate(1)
  }

  const handleSubmit = async () => {
    // Re-check the current step's gate so a stale/rapid click can't submit a
    // page whose required fields aren't satisfied.
    if (nextIsDisabled) return
    const currentSubmission: SubmissionObject = {
      photoImage: stripePayload?.submissionUrl || '',
      photoName: stripePayload?.submissionName || '',
      additionalFormFields: _.map(stripePayload.additionalFormFields, formField => {
        return {id: formField.id, value: formField.value, field_label: formField.name}
      }),
    }
    // Only advance to the success step once the submission is confirmed
    // recorded. On a failed cast (null vote / addVote rejection) onCastSubmission
    // resolves false and has already alerted the user, so we stay put — mirrors
    // the paid flow's advance gating in handlePaymentAttempt.
    const recorded = await onCastSubmission?.(currentSubmission)
    if (recorded) handleNext()
  }

  const NonUploadStepComponent = currentStep > 0 ? steps[currentStep].component : null

  const questionDescription = question?.description || null
  const submissionCardCta = (ballot?.photo_submission_settings as any)?.button_text || 'Begin Photo Submission'
  const submissionCardTitle = (ballot?.photo_submission_settings as any)?.title || customer?.name
  const submissionCardDescription = (ballot?.photo_submission_settings as any)?.description || 'Thank you for entering our photo contest! Select the button below to get started.'

  // Grid "Return to ballot" just closes; scroll variants also paginate
  const handleReturnToBallot = variant === 'flow-grid' ? onClose : handleCloseAndPaginate

  const variantClass = ctaStyles[`--${variant}`] || ''

  // ---------------------------------------------------------------------------
  // CTA card rendering per variant
  // ---------------------------------------------------------------------------
  const renderScrollCta = () => (
    <div className={`${ctaStyles['call-to-action-item']} ${variantClass} ${isCentered ? ctaStyles['--scroll-centered'] : ''}`}>
      <div className={ctaStyles['__logo-and-title']}>
        <img
          src={customer?.logoUrl || ''}
          className={ctaStyles['__customer-image']}
        />
        <h3 className={ctaStyles['__customer-name']}>{submissionCardTitle}</h3>
      </div>
      <ClientText clientText={submissionCardDescription}/>
      {canSubmit &&
        <button
          className={`button is-large ${ctaStyles['__call-to-action-button']}`}
          onClick={handleShowWizardClick}
        >
          {submissionCardCta}
        </button>
      }
      {!canSubmit &&
        <div className={`button is-large ${ctaStyles['__fake-button']}`}>
          Thank you for your submission!
        </div>
      }
    </div>
  )

  const renderFlowGridCta = () => (
    <div className={`${ctaStyles['call-to-action-item']} ${variantClass}`}>
      {canSubmit && <>
        <div
          className={ctaStyles['__content']}
          style={gridContainerStyles}
        >
          <div className={ctaStyles['__text-container']}>
            {customer?.logoUrl && (
              <img
                src={customer?.logoUrl}
                className={ctaStyles['__customer-image']}
              />
            )}
            <h3 className={ctaStyles['__customer-name']}>{submissionCardTitle}</h3>
            <ClientText clientText={submissionCardDescription}/>
          </div>
        </div>
        <div className={ctaStyles['__footer']}>
          <button
            className={`button is-fullwidth ${ctaStyles['__call-to-action-button']}`}
            onClick={handleShowWizardClick}
            style={{height: '51px'}}
          >
            {submissionCardCta}
          </button>
        </div>
      </>}
      {!canSubmit && <>
        <div
          className={ctaStyles['__content']}
          style={gridContainerStyles}
        >
          <div className={ctaStyles['__text-container']}>
            {customer?.logoUrl && (
              <img
                src={customer?.logoUrl}
                className={ctaStyles['__customer-image']}
              />
            )}
            <h3 className={ctaStyles['__customer-name']}>{submissionCardTitle}</h3>
            <ClientText clientText={submissionCardDescription}/>
          </div>
        </div>
        <div className={ctaStyles['__footer']}>
          <div
            className={`button is-fullwidth ${ctaStyles['__fake-button']}`}
            style={{height: '51px'}}
          >
            Thank you for your submission!
          </div>
        </div>
      </>}
    </div>
  )

  const renderFlowMasonryCta = () => (
    <div className={`${ctaStyles['call-to-action-item']} ${variantClass}`}>
      <div className={ctaStyles['__logo-and-title']}>
        <img
          src={customer?.logoUrl || undefined}
          className={ctaStyles['__customer-image']}
        />
        <h3 className={ctaStyles['__customer-name']}>{submissionCardTitle}</h3>
      </div>
      <div className={ctaStyles['__description']}>
        <ClientText clientText={submissionCardDescription}/>
      </div>
      {canSubmit &&
        <button
          className={`button is-large ${ctaStyles['__call-to-action-button']}`}
          onClick={handleShowWizardClick}
        >
          {submissionCardCta}
        </button>
      }
      {!canSubmit &&
        <div className={`button is-large ${ctaStyles['__fake-button']}`}>
          Thank you for your submission!
        </div>
      }
    </div>
  )

  const renderCta = () => {
    switch (variant) {
    case 'scroll': return renderScrollCta()
    case 'flow-grid': return renderFlowGridCta()
    case 'flow-masonry': return renderFlowMasonryCta()
    }
  }

  return question
    ? <>
      {!wizardIsVisible && renderCta()}
      {wizardIsVisible &&
        <div className={styles['submission-wizard']}>
          <div className={styles['__modal-header']}>
            <button
              type='button'
              onClick={onClose}
              disabled={closeIsDisabled}
              className='button is-medium'
              style={{
                position: 'absolute',
                top: '0',
                right: '0',
                height: '48px',
                width: '30px',
                fontSize: '24px',
                border: 'none',
                background: 'none',
              }}
            >
              {'\u2716'}
            </button>
          </div>
          {/* Always keep the upload step mounted so the Uploadcare widget
              retains its internal file list when the user navigates away and
              back via the Previous button. Hide it with CSS when inactive. */}
          <div style={currentStep === 0 ? undefined : hiddenStyle}>
            <UploaderComponent
              stripePayload={stripePayload}
              sendStripePayload={sendStripePayload}
              uploadcareFiles={uploadcareFiles}
              sendUploadcareFiles={sendUploadcareFiles}
              usesMultipleUpload={allowsMultiplePhotosPerSubmission}
            />
          </div>
          {NonUploadStepComponent && <NonUploadStepComponent
            voter={voter}
            stripePayload={stripePayload}
            sendStripePayload={sendStripePayload}
            uploadcareFiles={uploadcareFiles}
            sendUploadcareFiles={sendUploadcareFiles}
            paymentIsProcessing={paymentIsProcessing}
            sendPaymentIsProcessing={sendPaymentIsProcessing}
            onCastSubmission={onCastSubmission}
            isPaidSubmission={Boolean(paymentAmount)}
            submissionNameFieldLabel={(ballot.photo_submission_settings as any)?.name_field?.label || 'Name'}
            ballotAdditionalFormFields={ballotAdditionalFormFields}
            questionDescription={questionDescription}
            successMessage={successMessage}
            usesMultipleUpload={allowsMultiplePhotosPerSubmission}
            onNext={handleNext}
          />}
          <div className={styles['__modal-footer']}>
            {currentStep > 0 && currentStep < steps.length - 1 && (
              <button
                onClick={handlePrevious}
                className={`button is-large ${styles['__previous-button']}`}
                disabled={previousIsDisabled}
              >
                Previous
              </button>
            )}
            {currentStep < steps.length - 2 &&
              <button
                onClick={handleNext}
                className={`button is-large ${styles['__next-button']}`}
                disabled={nextIsDisabled}
              >
                Next
              </button>
            }
            {!paymentAmount && <>
              {currentStep === steps.length - 2 &&
                <button
                  onClick={handleSubmit}
                  className={`button is-large ${styles['__next-button']}`}
                  disabled={nextIsDisabled}
                >
                  Finish and nominate
                </button>
              }
            </>}
            {currentStep === steps.length - 1 &&
              <button
                onClick={handleReturnToBallot}
                className={`button is-large ${styles['__next-button']}`}
              >
                Return to ballot
              </button>
            }
          </div>
        </div>
      }
    </>
    : null
}

export default PhotoGallerySubmissionWizard
